import {getReportBaseUrl} from './common';
import jsurl from 'jsurl';
import moment from 'moment';

const PROPERTY_TYPE_MAP = {
  STRING: `string`,
  NUMBER: `number`,
  DATE: `datetime`,
  BOOLEAN: `boolean`,
  LIST: `list`,
  COHORT: `list`,
};

const MATH_ACTION_MAP = {
  TOTAL: `total`,
  UNIQUE: `unique`,
  AVERAGE: `average`,
  MEDIAN: `median`,
  MIN: `min`,
  MAX: `max`,
};

const TIME_UNIT_MAP = {
  HOUR: `hour`,
  DAY: `day`,
  WEEK: `week`,
  MONTH: `month`,
  QUARTER: `quarter`,
};

const PROPERTY_SOURCE_MAP = {
  EVENTS: `events`,
  PEOPLE: `people`,
};

const PROPERTY_FILTERS_OPERATOR_MAP = {
  AND: `all`,
  OR: `any`,
};

const STRING_FILTER_OPERATOR_MAP = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
  EQUALS: `equals`,
  DOES_NOT_EQUAL: `does not equal`,
  SET: `is set`,
  NOT_SET: `is not set`,
};

const NUMBER_FILTER_OPERATOR_MAP = {
  BETWEEN: `is between`,
  GREATER_THAN: `is greater than`,
  LESS_THAN: `is less than`,
  EQUAL_TO: `is equal to`,
};

const ABSOLUTE_DATE_FILTER_OPERATOR_MAP = {
  ON: `was on`,
  BETWEEN: `was between`,
};

const RELATIVE_DATE_FILTER_OPERATOR_MAP = {
  LESS_THAN: `was in the`,
  GREATER_THAN: null, // from segmentation, but not supported in insights
};

const BOOLEAN_FILTER_OPERATOR_MAP = {
  true: `is true`,
  false: `is false`,
};

const LIST_FILTER_OPERATOR_MAP = {
  CONTAINS: `contains`,
  DOES_NOT_CONTAIN: `does not contain`,
};

function valueForEvent(event) {
  if (event.startsWith(`$custom_event:`)) {
    const customEventId = Number(event.replace(/^\$custom_event:/, ``));
    return {custom: true, id: customEventId};
  } else {
    return {name: event};
  }
}

function getDisplayOptions({alertType}) {
  if (alertType === `ANOMALY`) {
    return {
      analysis: `linear`,
      chartType: `line`,
      plotStyle: `standard`,
      value: `absolute`,
    };
  }
}

function centerAlertInRange(queryStart, queryEnd, alertTimestamp) {
  // Calculate the number of days shown before and after the anomaly occured
  const startDate = moment(queryStart);
  let endDate = moment(queryEnd);
  const alertDate = moment.unix(alertTimestamp / 1000);
  const daysBefore = alertDate.diff(startDate, `days`);
  const daysAfter = endDate.diff(alertDate, `days`);

  // Adjust the start or end date so that the number of days shown before and after the alert are equal
  const daysDiff = daysAfter - daysBefore;
  if (daysDiff < 0) {
    endDate.add(-daysDiff, `days`);
  } else {
    startDate.subtract(daysDiff, `days`);
  }

  // If we've pushed the end date into the future, bring it back to today
  const today = moment();
  if (endDate > today) {
    endDate = today;
  }

  return {
    startDate: startDate.format(`YYYY-MM-DD`),
    endDate: endDate.format(`YYYY-MM-DD`),
  };
}

const exports = {};
export default exports;

exports.generateParams = function({bookmarkId, query, alertType, alertId, selectedExplains, alertTimestamp, shared}) {
  query = Object.assign(
    {
      showClauses: [],
      propertyFilters: [],
      groupByProperties: [],
    },
    query,
  );

  const showClauses = query.showClauses.map((showClause) => {
    if (showClause.showEvent) {
      const showEvent = showClause.showEvent;
      return {
        math: MATH_ACTION_MAP[showEvent.action],
        resourceType: `all`,
        value: valueForEvent(showEvent.event),
      };
    } else if (showClause.showEventProperty) {
      const showEventProperty = showClause.showEventProperty;
      const property = showEventProperty.property;
      return {
        math: MATH_ACTION_MAP[showEventProperty.action],
        resourceType: `all`,
        property: {
          name: property.name,
          resourceType: PROPERTY_SOURCE_MAP[property.source],
          type: PROPERTY_TYPE_MAP[property.type],
        },
        value: valueForEvent(showEventProperty.event),
      };
    } else if (showClause.showPeopleProperty) {
      const showPeopleProperty = showClause.showPeopleProperty;
      const property = showPeopleProperty.property;
      return {
        math: MATH_ACTION_MAP[showPeopleProperty.action],
        resourceType: `people`,
        property: {
          name: property.name,
          resourceType: PROPERTY_SOURCE_MAP[property.source],
          type: PROPERTY_TYPE_MAP[property.type],
        },
      };
    }
  });

  const groupClauses = query.groupByProperties.map((groupByProperty) => {
    const property = groupByProperty.property;
    const groupClause = {
      propertyType: PROPERTY_TYPE_MAP[property.type],
      typeCast: property.typecast ? PROPERTY_TYPE_MAP[property.typecast] : null,
      resourceType: PROPERTY_SOURCE_MAP[property.source],
      value: property.name,
    };
    if (groupByProperty.timeUnit) {
      groupClause.unit = TIME_UNIT_MAP[groupByProperty.timeUnit];
    }
    return groupClause;
  });

  const filterClauses = query.propertyFilters
    .map((propertyFilter) => {
      let filterClause = {};
      if (propertyFilter.stringFilter) {
        const {operator, operands} = propertyFilter.stringFilter;
        let filterValue;
        if ([`EQUALS`, `DOES_NOT_EQUAL`].includes(operator)) {
          filterValue = operands;
        } else if ([`CONTAINS`, `DOES_NOT_CONTAIN`].includes(operator)) {
          filterValue = operands[0];
        } else {
          filterValue = null;
        }
        filterClause = Object.assign(filterClause, {
          filterOperator: STRING_FILTER_OPERATOR_MAP[operator],
          filterValue,
        });
      } else if (propertyFilter.numberFilter) {
        const {operator, operand1, operand2} = propertyFilter.numberFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: NUMBER_FILTER_OPERATOR_MAP[operator],
          filterValue: operator === `BETWEEN` ? [operand1, operand2] : operand1,
        });
      } else if (propertyFilter.absoluteDateFilter) {
        const {operator, operand1, operand2} = propertyFilter.absoluteDateFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: ABSOLUTE_DATE_FILTER_OPERATOR_MAP[operator],
          filterValue: operator === `BETWEEN` ? [operand1, operand2] : operand1,
        });
      } else if (propertyFilter.relativeDateFilter) {
        const {operator, operand, timeUnit} = propertyFilter.relativeDateFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: RELATIVE_DATE_FILTER_OPERATOR_MAP[operator],
          filterValue: operand,
          filterDateUnit: TIME_UNIT_MAP[timeUnit],
        });
      } else if (propertyFilter.booleanFilter) {
        const {operand} = propertyFilter.booleanFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: BOOLEAN_FILTER_OPERATOR_MAP[operand],
        });
      } else if (propertyFilter.listFilter) {
        const {operator, operand} = propertyFilter.listFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: LIST_FILTER_OPERATOR_MAP[operator],
          filterValue: operand,
        });
      } else if (propertyFilter.cohortFilter) {
        const {operator, operand} = propertyFilter.cohortFilter;
        filterClause = Object.assign(filterClause, {
          filterOperator: LIST_FILTER_OPERATOR_MAP[operator],
          filterValue: [operand],
        });
      } else {
        return;
      }

      const property = propertyFilter.property;
      filterClause = Object.assign(filterClause, {
        value: property.name,
        filterType: PROPERTY_TYPE_MAP[property.typecast || property.type],
        resourceType: PROPERTY_SOURCE_MAP[property.source],
      });

      return filterClause;
    })
    .filter((x) => x);

  const filterSection = {clauses: filterClauses};
  if (query.propertyFiltersOperator) {
    filterSection.determiner = PROPERTY_FILTERS_OPERATOR_MAP[query.propertyFiltersOperator];
  }

  const {startDate, endDate} = centerAlertInRange(query.dateRange.from, query.dateRange.to, alertTimestamp);

  const timeClause = {value: [startDate, endDate]};
  if (query.timeUnit) {
    timeClause.unit = TIME_UNIT_MAP[query.timeUnit];
  }
  const timeClauses = [timeClause];

  return {
    smartHub: {alertType, alertId, bookmarkId, explains: selectedExplains},
    displayOptions: getDisplayOptions({alertType}),
    sections: {
      show: {clauses: showClauses},
      group: {clauses: groupClauses},
      filter: filterSection,
      time: {clauses: timeClauses},
    },
    shared: shared ? `smarthub` : undefined,
  };
};

// See InsightsQuery in clj/service/smart-hub/protos/smart_hub.proto for the query format
exports.generatePermalink = function(
  {bookmarkId, query, alertType, alertId, projectId, selectedExplains, alertTimestamp, shared, workspaceId},
  absolute = false,
) {
  // In order to stub/spy on generateParams when generatePermalink is called, we need to access
  // it from a enclosed namespace (i.e. exports).
  const urlParams = exports.generateParams({
    bookmarkId,
    query,
    alertType,
    alertId,
    selectedExplains,
    alertTimestamp,
    shared,
  });
  let link = `${getReportBaseUrl({
    projectId,
    workspaceId,
  })}/insights/#${jsurl.stringify(urlParams)}`;
  if (absolute) {
    const rootURL = `${window.location.protocol}//${window.location.host}`;
    link = rootURL + link;
  }
  return link;
};
